import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { employeeApi } from '../../api/employeeApi';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon, 
  CameraIcon, 
  DocumentArrowUpIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const resumeInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    role: 'Staff',
    salaryType: 'monthly',
    salaryAmount: '',
    profilePicture: '',
    resume: '',
    documentId: '',
    emergencyContact: '',
    address: '',
    status: 'active'
  });

  // Basic ImageKit upload directly from client side (For production, implement signature generation)
  const handleFileUpload = async (file, type) => {
    const loadingToast = toast.loading(`Uploading ${type}...`);
    try {
      // First get authentication params from backend
      const authRes = await axios.get('/api/v1/imagekit'); // Assuming proxy passes this
      const { signature, expire, token } = authRes.data;

      // Construct FormData for ImageKit REST API
      const data = new FormData();
      data.append('file', file);
      data.append('fileName', file.name);
      data.append('signature', signature);
      data.append('expire', expire);
      data.append('token', token);
      data.append('publicKey', import.meta.env.VITE_IMAGEKIT_PUBLIC_KEY || 'public_dummy'); 
      data.append('folder', '/khatabook/employees');

      const uploadRes = await axios.post('https://upload.imagekit.io/api/v1/files/upload', data);
      
      if (uploadRes.data && uploadRes.data.url) {
        setFormData(prev => ({
          ...prev,
          [type]: uploadRes.data.url
        }));
        toast.success(`${type} uploaded successfully!`);
      }
    } catch (error) {
      console.error('Upload Error:', error);
      toast.error(`Failed to upload ${type}. Please check ImageKit configuration.`);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  useEffect(() => {
    if (isEditMode) {
      const fetchEmployee = async () => {
        try {
          const res = await employeeApi.getEmployeeDetails(id);
          const emp = res.data.data;
          setFormData({
            name: emp.name || '',
            phone: emp.phone || '',
            role: emp.role || 'Staff',
            salaryType: emp.salaryType || 'monthly',
            salaryAmount: emp.salaryAmount || '',
            profilePicture: emp.profilePicture || '',
            resume: emp.resume || '',
            documentId: emp.documentId || '',
            emergencyContact: emp.emergencyContact || '',
            address: emp.address || '',
            status: emp.status || 'active'
          });
        } catch (error) {
          toast.error("Error loading employee details");
          navigate('/employees');
        } finally {
          setLoading(false);
        }
      };
      fetchEmployee();
    }
  }, [id, navigate, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        salaryAmount: Number(formData.salaryAmount)
      };

      if (isEditMode) {
        await employeeApi.updateEmployee(id, payload);
        toast.success("Employee updated successfully!");
      } else {
        await employeeApi.addEmployee(payload);
        toast.success("Employee added successfully!");
      }
      navigate('/employees');
    } catch (error) {
      console.error("Submit error", error);
      toast.error(error.response?.data?.message || "Something went wrong!");
    } finally {
      setSubmitting(false);
    }
  };

    if (loading) {
    return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#dc343b]"></div></div>;
  }

  return (
    <div className="w-full pb-20 max-w-full mx-auto space-y-6 pt-4 px-0 sm:px-4">
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={() => navigate('/employees')}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white">
            {isEditMode ? 'Edit Employee Profile' : 'Add New Employee'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Fill in the details below to complete the employee registration.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* Profile Picture Upload Section */}
        <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/10 dark:to-orange-900/10 opacity-50"></div>
          
          <div className="relative z-10 flex flex-col items-center">
            <div 
              className="w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-700 border-4 border-white dark:border-gray-800 shadow-xl overflow-hidden cursor-pointer relative group flex items-center justify-center"
              onClick={() => fileInputRef.current?.click()}
            >
              {formData.profilePicture ? (
                <img src={formData.profilePicture} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl text-gray-300 font-bold">{formData.name ? formData.name.charAt(0).toUpperCase() : '?'}</span>
              )}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <CameraIcon className="w-8 h-8 text-white" />
              </div>
            </div>
            <p className="mt-4 text-sm font-semibold text-[#dc343b] dark:text-red-400 cursor-pointer hover:underline" onClick={() => fileInputRef.current?.click()}>
              {formData.profilePicture ? 'Change Photo' : 'Upload Photo'}
            </p>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept="image/*"
              onChange={(e) => {
                if (e.target.files?.[0]) handleFileUpload(e.target.files[0], 'profilePicture');
              }}
            />
          </div>
        </div>

        {/* Form Grid */}
        <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-3xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-gray-100 dark:border-gray-700 space-y-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-4 flex items-center gap-2">
            <span className="bg-red-100 dark:bg-red-900/30 text-[#dc343b] dark:text-red-400 w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
            Personal Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Full Name *</label>
              <input type="text" required
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
              <input type="tel" 
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Full Address</label>
              <input type="text" 
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})}
                placeholder="House No, Street, City"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Emergency Contact</label>
              <input type="tel" 
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                value={formData.emergencyContact} onChange={e => setFormData({...formData, emergencyContact: e.target.value})}
                placeholder="Family member's number"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Document ID (Aadhar/PAN)</label>
              <input type="text" 
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                value={formData.documentId} onChange={e => setFormData({...formData, documentId: e.target.value})}
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-3xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-gray-100 dark:border-gray-700 space-y-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-4 flex items-center gap-2">
            <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
            Work & Salary
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Designation / Role *</label>
              <input type="text" required
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})}
                placeholder="e.g. Manager, Cashier, Helper"
              />
            </div>
            {isEditMode && (
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                <select 
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                  value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}
                >
                  <option value="active">Active (Currently Working)</option>
                  <option value="inactive">Inactive (Left Job)</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Salary Type *</label>
              <select 
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                value={formData.salaryType} onChange={e => setFormData({...formData, salaryType: e.target.value})}
              >
                <option value="monthly">Monthly Fixed</option>
                <option value="daily">Daily Wage (Dihaadi)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                {formData.salaryType === 'monthly' ? 'Monthly Salary (₹) *' : 'Per Day Wage (₹) *'}
              </label>
              <input type="number" required min="0"
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 font-bold text-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b]"
                value={formData.salaryAmount} onChange={e => setFormData({...formData, salaryAmount: e.target.value})}
              />
            </div>

            {/* Resume Upload */}
            <div className="md:col-span-2 mt-2">
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Resume / CV (PDF/Image)</label>
              <div 
                onClick={() => resumeInputRef.current?.click()}
                className={`w-full border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-colors ${formData.resume ? 'border-green-400 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                {formData.resume ? (
                  <div className="flex flex-col items-center text-green-600 dark:text-green-400">
                    <CheckBadgeIcon className="w-10 h-10 mb-2" />
                    <span className="font-bold">Resume Uploaded Successfully</span>
                    <span className="text-xs mt-1 underline">Click to upload a different file</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-gray-500">
                    <DocumentArrowUpIcon className="w-10 h-10 mb-2" />
                    <span className="font-bold">Click to Upload Resume</span>
                    <span className="text-xs mt-1">PDF, JPG, or PNG</span>
                  </div>
                )}
              </div>
              <input 
                type="file" 
                ref={resumeInputRef} 
                className="hidden" 
                onChange={(e) => {
                  if (e.target.files?.[0]) handleFileUpload(e.target.files[0], 'resume');
                }}
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#dc343b] hover:bg-[#b91c1c] text-white py-4 rounded-2xl font-extrabold text-lg transition-all shadow-lg shadow-red-500/30 disabled:opacity-70"
        >
          {submitting ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Complete Registration')}
        </button>

      </form>
    </div>
  );
};

export default EmployeeForm;
